import React, { useEffect, useMemo, useState } from 'react';
import { X, Truck, Minus, Plus, ShoppingCart, ShoppingBag } from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import api from '@/lib/api';
import SafeImg from '@/components/SafeImg';

const won = (n) => `${(Number(n) || 0).toLocaleString('ko-KR')}원`;

/**
 * Bottom sheet (mobile) / centred modal (desktop) for picking product options,
 * quantity, and summarising the order before adding to cart or checking out.
 *
 * Props:
 *   product       — /api/products/{id} response (contains options, shipping_policy, seller_id …)
 *   open          — boolean
 *   intent        — 'cart' | 'buy'  (used to bias which button gets primary emphasis)
 *   onClose()
 */
const OptionSheet = ({ product, open, intent = 'cart', onClose, attribution = null, orderEndpoint = null }) => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState({});  // { [group_name]: value_label }
  const [qty, setQty] = useState(1);
  const [busy, setBusy] = useState(false);
  // STEP 7-INVENTORY-H · Buyer 옵션 조합별 재고 (variant 데이터 미보유 상품 대비 null-safe)
  const [variantInfo, setVariantInfo] = useState(null);   // { unlimited, has_variant_data, variants[] }

  const optionGroups = useMemo(() => (product?.options || []).filter((g) => g?.name), [product]);
  const optionsRequired = optionGroups.length > 0;
  const allSelected = !optionsRequired || optionGroups.every((g) => selected[g.name]);

  // STEP 7-INVENTORY-H · 부분 선택에서도 안전한 variant 품절 판정
  //  · variantInfo === null (아직 조회 중 또는 실패) → 판정 skip · legacy `v.stock` fallback
  //  · variantInfo.unlimited === true → 절대 disable 하지 않음 (무제한 상품)
  //  · variantInfo.has_variant_data === false → variant 미입력 상품 · legacy fallback
  //  · variantInfo 정상: 이 값 v 를 선택했을 때 나올 수 있는 모든 조합의 available 이 전부 0 이면 disable
  const isValueSoldOut = (group, v) => {
    // Legacy fallback (variantInfo 없거나 미입력 상품)
    const legacy = Number(v.stock) === 0;
    if (!variantInfo || variantInfo.unlimited) return false;
    if (!variantInfo.has_variant_data) return legacy;
    const partial = { ...selected, [group.name]: v.label };
    const matched = (variantInfo.variants || []).filter((row) => {
      const opt = row.option || {};
      return Object.keys(partial).every((k) => opt[k] === partial[k]);
    });
    if (!matched.length) return false;    // 미입력 조합 · UX 상 열어 둠 · atomic reserve 가 최종 보호
    return matched.every((r) => Number(r.available) === 0);
  };

  // Compute surcharge & summary derived state.
  const summary = useMemo(() => {
    if (!product) return { surcharge: 0, subtotal: 0, shipping: 0, total: 0, soldOut: false };
    let surcharge = 0;
    let soldOut = false;
    optionGroups.forEach((g) => {
      const chosenLabel = selected[g.name];
      const v = (g.values || []).find((x) => x.label === chosenLabel);
      if (v) surcharge += Number(v.extra_price || 0);
    });
    // STEP 7-INVENTORY-H · 최종 선택된 조합의 실 재고 확인 (variantInfo 있을 때만)
    if (variantInfo && !variantInfo.unlimited && variantInfo.has_variant_data && allSelected && optionsRequired) {
      const row = (variantInfo.variants || []).find((r) => {
        const opt = r.option || {};
        return Object.keys(selected).every((k) => opt[k] === selected[k]);
      });
      if (row && Number(row.available) === 0) soldOut = true;
    } else if (optionsRequired) {
      // legacy fallback
      optionGroups.forEach((g) => {
        const chosenLabel = selected[g.name];
        const v = (g.values || []).find((x) => x.label === chosenLabel);
        if (v && Number(v.stock) === 0) soldOut = true;
      });
    }
    const unitPrice = Number(product.sale_price || 0) + surcharge;
    const subtotal = unitPrice * qty;
    // Shipping: use seller shipping policy attached to the product detail response.
    const sp = product.shipping_policy || {};
    let shipping = 0;
    if (product.shipping_option === 'free' || sp.shipping_type === 'free') shipping = 0;
    else if (sp.shipping_type === 'conditional' && subtotal >= Number(sp.free_threshold || 0)) shipping = 0;
    else shipping = Number(sp.base_fee || 0);
    return { surcharge, subtotal, shipping, total: subtotal + shipping, soldOut };
  }, [product, optionGroups, selected, qty, variantInfo, allSelected, optionsRequired]);

  // STEP MINI-ORDER-SAFETY-1 · A14 — Idempotency-Key per order intent.
  // 같은 "주문 의도" (open + 상품) 안에서는 ref 가 안정 · 네트워크 재시도도 동일 키 전송.
  // sheet 를 닫았다 재오픈하거나 상품이 바뀌면 새 키 발급.
  const idemKeyRef = React.useRef(null);

  // Reset picker whenever the sheet is (re)opened.
  useEffect(() => {
    if (open) {
      setSelected({}); setQty(1);
      // 새 주문 의도 시작 — crypto.randomUUID 우선, 미지원 환경은 timestamp+random fallback.
      const uuid = (typeof crypto !== 'undefined' && crypto.randomUUID)
        ? crypto.randomUUID()
        : `${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
      idemKeyRef.current = `mini-${uuid}`;
    }
  }, [open, product?.product_id]);

  // STEP 7-INVENTORY-H · variantInfo 1회 조회 (open + product 변경 시)
  //  · 실패해도 sheet 는 열림 · legacy fallback 유지 (variantInfo=null)
  //  · 옵션 없는 상품은 API 호출 skip (불필요)
  useEffect(() => {
    if (!open || !product?.product_id) { setVariantInfo(null); return; }
    if (!(product.options || []).length) { setVariantInfo(null); return; }
    let cancelled = false;
    api.get(`/public/products/${product.product_id}/variants`)
      .then(({ data }) => { if (!cancelled) setVariantInfo(data || null); })
      .catch(() => { if (!cancelled) setVariantInfo(null); });
    return () => { cancelled = true; };
  }, [open, product?.product_id]);

  // Close on browser back button (mobile UX from the spec).
  useEffect(() => {
    if (!open) return;
    window.history.pushState({ __sheet: true }, '');
    const onPop = () => onClose?.();
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, [open, onClose]);

  const requireSelection = () => {
    if (!allSelected) { toast.error('필수 옵션을 선택해주세요.'); return false; }
    if (summary.soldOut) { toast.error('선택한 옵션은 품절입니다.'); return false; }
    return true;
  };

  const doAddToCart = async () => {
    if (!requireSelection() || busy) return;
    setBusy(true);
    try {
      await api.post('/cart/items', {
        product_id: product.product_id,
        quantity: qty,
        option: selected,
        broadcast_id: product.broadcast_id || null,  // preserved for TikTok/IG/YT live commerce flow
        // Prod-S4 · Shorts 유입 상품이면 attribution 을 cart-item 에 저장 (일반 진입은 null)
        ...(attribution ? { attribution } : {}),
      });
      try { window.dispatchEvent(new Event('livemoa:cart-updated')); } catch {}
      // STEP 8-I-4 · LIVE 방송에서 담기 성공하면 return-to-live 저장 (Cart/Checkout 상단 CTA 용)
      if (product.broadcast_id) {
        try { sessionStorage.setItem('livemoa:return_to_live', `/live-view/${product.broadcast_id}`); } catch {}
      }
      // STEP 8-I-4A · Toast/Snackbar 없음 · 성공 알림은 오직 댓글 흐름의 🛒 Activity 로만.
      onClose?.();
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('구매하려면 로그인이 필요합니다.');
        const returnUrl = encodeURIComponent(window.location.pathname + window.location.search);
        navigate(`/login?return_url=${returnUrl}`);
      }
      else toast.error(err?.response?.data?.detail || '담기에 실패했습니다.');
    } finally { setBusy(false); }
  };

  const doBuyNow = async () => {
    if (!requireSelection() || busy) return;
    // STEP 8-I-4A FINAL · In-LIVE 인라인 주문 · 방송 이탈 없음.
    //   LIVE (product.broadcast_id 있음) 이면 신규 /inline-order 로 즉시 접수.
    //   저장된 배송지 없거나 실패면 기존 checkout page 로 fallback.
    if (product.broadcast_id) {
      try { sessionStorage.setItem('livemoa:return_to_live', `/live-view/${product.broadcast_id}`); } catch {}
      setBusy(true);
      try {
        // STEP MINI-EXTERNAL-SESSION-IMPL-1 · 외부 SNS 미니주문은 schedule-based
        // endpoint 로 직접 접수. orderEndpoint prop 이 주입되면 그 URL 사용 ·
        // 아니면 기존 session-based inline-order (LIVEMOA Studio) 로 fallback.
        const url = orderEndpoint
          || `/public/streaming/sessions/${product.broadcast_id}/inline-order`;
        // STEP MINI-ORDER-SAFETY-2 · 외부 미니주문 + 자사 LIVE inline-order 양쪽
        // 모두 X-Idempotency-Key 전송. 서버는 scope 를 자동 분기 (mini_order /
        // self_live_mini).
        const reqConfig = (idemKeyRef.current)
          ? { headers: { 'X-Idempotency-Key': idemKeyRef.current } }
          : undefined;
        const { data } = await api.post(
          url,
          {
            product_id: product.product_id, quantity: qty, option: selected,
            ...(attribution ? { attribution } : {}),
          },
          reqConfig,
        );
        if (data && data.ok) {
          // 방송 화면 유지 · Toast 없음 · 🎉 Activity 는 order_created hook 이 자동 발화.
          onClose?.();
          return;
        }
        // needs_shipping → 기존 checkout page 로 fallback (기존 로직).
      } catch (err) {
        if (err?.response?.status === 401) {
          toast.error('구매하려면 로그인이 필요합니다.');
          onClose?.();
          navigate('/login?return_url=%2Fcheckout');
          return;
        }
        // 다른 오류 → 사용자에게 알린 뒤 fallback 도 시도 (배송지 필요 등).
        const msg = err?.response?.data?.detail || '주문 접수에 실패했어요. 배송지를 확인해주세요.';
        toast.error(msg);
      } finally { setBusy(false); }
      // fallback → pending_order 저장 후 기존 checkout 이동.
    }

    // LIVE 아님 (또는 inline fallback) · 기존 Checkout 페이지 flow.
    const payload = {
      product_id: product.product_id,
      seller_id: product.seller_id,
      broadcast_id: product.broadcast_id || null,
      name: product.name,
      image: product.image,
      option: selected,
      quantity: qty,
      unit_price: Number(product.sale_price) + summary.surcharge,
      subtotal: summary.subtotal,
      shipping: summary.shipping,
      total: summary.total,
      // Prod-S4 · Shorts attribution 전달 · Checkout 이 POST /orders body 에 포함
      ...(attribution ? { attribution } : {}),
    };
    try { sessionStorage.setItem('livemoa:pending_order', JSON.stringify(payload)); } catch {}
    try {
      await api.get('/auth/me');
    } catch (err) {
      if (err?.response?.status === 401) {
        toast.error('구매하려면 로그인이 필요합니다.');
        onClose?.();
        navigate('/login?return_url=%2Fcheckout');
        return;
      }
    }
    onClose?.();
    navigate('/checkout');
  };

  // STEP 7-INVENTORY-H 소수정 · 부분 품절 안내 (해당 그룹에서 선택한 값 기준으로 다른 그룹으로 구매 가능한지)
  //  · 옵션 2개 이상 상품 · variantInfo 정상 · unlimited=false 일 때만
  //  · 매칭 조합 중 일부는 available>0, 일부는 available===0 인 "혼합" 상태에서만 표시
  //  · 모두 정상이면 표시 X · 모두 품절이면 어차피 다른 값으로 유도할 여지 없어 표시 X
  //  · 한국어 조사 자동 매칭 (은/는 · 로/으로) · 배치임 검사로 자연스러운 문구 출력
  const _kJong = (w) => {
    if (!w) return -1;
    const c = w[w.length - 1].charCodeAt(0);
    if (c < 0xAC00 || c > 0xD7A3) return -1;
    return (c - 0xAC00) % 28;
  };
  const _eunNeun = (w) => (_kJong(w) > 0 ? '은' : '는');
  const _roEuro = (w) => { const j = _kJong(w); return (j <= 0 || j === 8) ? '로' : '으로'; };
  const partialHintFor = (g) => {
    if (!variantInfo || variantInfo.unlimited || !variantInfo.has_variant_data) return null;
    const chosen = selected[g.name];
    if (!chosen) return null;
    const matched = (variantInfo.variants || []).filter((r) => (r.option || {})[g.name] === chosen);
    if (matched.length < 2) return null;
    const someAvail = matched.some((r) => Number(r.available) > 0);
    const someSold = matched.some((r) => Number(r.available) === 0);
    if (!someAvail || !someSold) return null;
    const otherKeys = new Set();
    matched.forEach((r) => Object.keys(r.option || {}).forEach((k) => { if (k !== g.name) otherKeys.add(k); }));
    const otherName = [...otherKeys][0];
    if (!otherName) return null;
    return `이 ${g.name}${_eunNeun(g.name)} 다른 ${otherName}${_roEuro(otherName)} 구매할 수 있어요.`;
  };

  if (!open || !product) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center" role="dialog" aria-modal="true" aria-label="상품 옵션 선택" data-testid="option-sheet">
      <div className="absolute inset-0 bg-black/60 animate-in fade-in duration-150" onClick={onClose} />
      <div className="relative w-full sm:max-w-md max-h-[85vh] sm:max-h-[80vh] bg-white dark:bg-[#141414] rounded-t-2xl sm:rounded-2xl shadow-2xl flex flex-col animate-in slide-in-from-bottom sm:zoom-in-95 duration-200">
        {/* Drag handle (mobile) */}
        <div className="sm:hidden flex justify-center pt-2 pb-1"><span className="h-1 w-10 rounded-full bg-gray-300 dark:bg-gray-700" /></div>
        <div className="flex items-center justify-between px-4 sm:px-5 pt-2 sm:pt-4">
          <div className="flex items-center gap-3 min-w-0">
            <div className="w-11 h-11 rounded-lg overflow-hidden bg-gray-100 dark:bg-gray-800 shrink-0"><SafeImg src={product.image} kind="product" alt="" className="w-full h-full object-cover" /></div>
            <div className="min-w-0">
              <p className="text-sm font-bold truncate" data-testid="sheet-product-name">{product.name}</p>
              <p className="text-xs font-black text-[#FF3B30]" data-testid="sheet-product-price">{won(product.sale_price)}</p>
            </div>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 hover:bg-black/5 dark:hover:bg-white/10 rounded-full" aria-label="닫기" data-testid="sheet-close"><X size={16} /></button>
        </div>

        <div className="flex-1 overflow-y-auto px-4 sm:px-5 py-4 space-y-4">
          {/* Option groups */}
          {optionGroups.map((g) => (
            <div key={g.name} data-testid={`sheet-group-${g.name}`}>
              <p className="text-xs font-black text-gray-700 dark:text-gray-300 mb-1.5">{g.name}{selected[g.name] && <span className="ml-1 text-[#FF3B30] font-black">{selected[g.name]}</span>}</p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-1.5">
                {(g.values || []).map((v) => {
                  const isSelected = selected[g.name] === v.label;
                  const isSoldOut = isValueSoldOut(g, v);
                  return (
                    <button key={v.label} type="button" disabled={isSoldOut}
                      onClick={() => setSelected((s) => ({ ...s, [g.name]: v.label }))}
                      className={`relative px-2 py-2 rounded-lg text-xs font-bold border transition-all ${
                        isSoldOut
                          ? 'border-black/10 dark:border-white/10 text-gray-300 dark:text-gray-600 line-through cursor-not-allowed'
                          : isSelected
                            ? 'border-[#FF3B30] border-2 bg-[#FF3B30]/5 text-[#FF3B30]'
                            : 'border-black/15 dark:border-white/15 text-black dark:text-white hover:border-[#FF3B30]'
                      }`}
                      data-testid={`sheet-option-${g.name}-${v.label}`}
                      aria-pressed={isSelected}
                    >
                      <span className="block">{v.label}</span>
                      {v.extra_price ? <span className="block text-[10px] font-medium mt-0.5 text-gray-500">+{won(v.extra_price)}</span> : null}
                      {isSoldOut && <span className="absolute top-0.5 right-0.5 text-[9px] font-black text-[#FF3B30]">품절</span>}
                    </button>
                  );
                })}
              </div>
              {(() => {
                const hint = partialHintFor(g);
                return hint ? (
                  <p className="mt-1.5 text-[11px] text-gray-500 leading-snug break-keep" data-testid={`sheet-partial-hint-${g.name}`}>
                    {hint}
                  </p>
                ) : null;
              })()}
            </div>
          ))}

          {/* Quantity */}
          <div className="flex items-center justify-between pt-1">
            <p className="text-xs font-black text-gray-700 dark:text-gray-300">수량</p>
            <div className="inline-flex items-center border border-black/10 dark:border-white/10 rounded-full">
              <button type="button" onClick={() => setQty((q) => Math.max(1, q - 1))} className="p-1.5 hover:text-[#FF3B30]" aria-label="수량 감소" data-testid="sheet-qty-minus"><Minus size={12} /></button>
              <span className="px-3 text-sm font-bold min-w-8 text-center" data-testid="sheet-qty">{qty}</span>
              <button type="button" onClick={() => setQty((q) => q + 1)} className="p-1.5 hover:text-[#FF3B30]" aria-label="수량 증가" data-testid="sheet-qty-plus"><Plus size={12} /></button>
            </div>
          </div>

          {/* Selection summary */}
          {allSelected && (
            <div className="rounded-lg bg-black/[0.03] dark:bg-white/[0.05] p-3 text-xs space-y-1" data-testid="sheet-summary">
              <div className="flex justify-between text-gray-500"><span>선택 옵션</span><span className="font-bold text-gray-800 dark:text-gray-200">{Object.values(selected).join(' / ') || '없음'}</span></div>
              <div className="flex justify-between text-gray-500"><span>수량</span><span className="font-bold text-gray-800 dark:text-gray-200">{qty}개</span></div>
              <div className="flex justify-between text-gray-500"><span>상품금액</span><span className="font-bold text-gray-800 dark:text-gray-200">{won(summary.subtotal)}</span></div>
              <div className="flex justify-between text-gray-500"><span className="inline-flex items-center gap-1"><Truck size={11} /> 배송비</span><span className="font-bold text-gray-800 dark:text-gray-200">{summary.shipping === 0 ? '무료' : won(summary.shipping)}</span></div>
              <div className="flex justify-between pt-1 border-t border-black/5 dark:border-white/10"><span className="font-black">예상 결제금액</span><span className="text-base font-black text-[#FF3B30]" data-testid="sheet-total">{won(summary.total)}</span></div>
            </div>
          )}
        </div>

        {/* Actions pinned bottom */}
        <div className="flex gap-2 p-3 sm:p-4 border-t border-black/5 dark:border-white/10 bg-white dark:bg-[#141414]">
          <button type="button" onClick={doAddToCart} disabled={busy}
            className={`flex-1 py-3 rounded-full text-sm font-bold inline-flex items-center justify-center gap-1.5 ${intent === 'cart' ? 'bg-[#FF3B30] hover:bg-[#D32F2F] text-white' : 'border border-black/15 dark:border-white/15 text-black dark:text-white hover:border-[#FF3B30] hover:text-[#FF3B30]'}`}
            data-testid="sheet-add-cart"
          ><ShoppingCart size={14} /> 장바구니</button>
          <button type="button" onClick={doBuyNow}
            className={`flex-1 py-3 rounded-full text-sm font-bold inline-flex items-center justify-center gap-1.5 ${intent === 'buy' ? 'bg-[#FF3B30] hover:bg-[#D32F2F] text-white' : 'bg-black text-white dark:bg-white dark:text-black hover:bg-[#FF3B30]'}`}
            data-testid="sheet-buy-now"
          ><ShoppingBag size={14} /> 주문하기</button>
        </div>
      </div>
    </div>
  );
};

export default OptionSheet;
